import React, { useCallback, useEffect, useMemo, useState } from 'react';
import reportFormatService from '../../services/reportFormatService';
import PathologyReportDocument from '../../components/pathologyReport/PathologyReportDocument';
import { REPORT_FORMATS, DEFAULT_REPORT_FORMAT } from '../../components/pathologyReport/reportHelpers';
import './ReportFormatMasterPage.css';

const SAMPLE_PATIENT = {
  patientName: 'SAMPLE PATIENT', patientId: 'UHID-000123', ageGender: '34 Y / Male', ledgerTransactionNo: 'SAMPLE-0001',
  doctorName: 'Dr. Sample Referrer', barcodeNo: '1000001', sampleTypeName: 'Serum', sampleReceiveDate: new Date().toISOString(),
  approvedDate: new Date().toISOString(),
};
const SAMPLE_TESTS = [
  { testId: 1, investigationName: 'Lipid Profile', departmentName: 'Biochemistry', approved: 1 },
  { testId: 2, investigationName: 'Complete Blood Count', departmentName: 'Haematology', approved: 1 },
];
const SAMPLE_OBS = {
  1: [
    { labObservationId: 11, name: 'Total Cholesterol', currentValue: '214', unit: 'mg/dL', minReading: '0', maxReading: '200', currentFlag: 'H' },
    { labObservationId: 12, name: 'HDL Cholesterol', currentValue: '48', unit: 'mg/dL', minReading: '40', maxReading: '60', currentFlag: 'N' },
    { labObservationId: 13, name: 'Triglycerides', currentValue: '132', unit: 'mg/dL', minReading: '0', maxReading: '150', currentFlag: 'N' },
  ],
  2: [
    { labObservationId: 21, name: 'Haemoglobin', currentValue: '11.2', unit: 'g/dL', minReading: '13', maxReading: '17', currentFlag: 'L' },
    { labObservationId: 22, name: 'Total WBC Count', currentValue: '7400', unit: '/cumm', minReading: '4000', maxReading: '11000', currentFlag: 'N' },
    { labObservationId: 23, name: 'Platelet Count', currentValue: '2.4', unit: 'lakh/cumm', minReading: '1.5', maxReading: '4.1', currentFlag: 'N' },
  ],
};
const SAMPLE_SIGNATURE = { doctorName: 'Dr. Sample Pathologist', designation: 'Consultant Pathologist', qualification: 'MD (Pathology)', regAuthority: 'Reg', medicalRegNo: '00000' };

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

export const ReportFormatMasterPage = () => {
  const [centres, setCentres] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [choice, setChoice] = useState(DEFAULT_REPORT_FORMAT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportFormatService.listCentres();
      if (res.success) {
        setCentres(res.data || []);
        setSelectedId((cur) => cur ?? res.data?.[0]?.centreId ?? null);
      } else {
        setMessage({ type: 'error', text: res.message || 'Could not load the centres.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: messageOf(err, 'Could not load the centres.') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => centres.find((c) => c.centreId === selectedId) || null, [centres, selectedId]);
  useEffect(() => { if (selected) setChoice(selected.formatCode); }, [selected]);

  const dirty = !!selected && choice !== selected.formatCode;

  const save = async () => {
    if (!selected || saving || !dirty) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await reportFormatService.saveForCentre(selected.centreId, choice);
      if (res.success) {
        setCentres((list) => list.map((c) => (c.centreId === selected.centreId ? res.data : c)));
        setMessage({ type: 'success', text: `Report layout for ${selected.centreName} saved. Reports of this centre now print in this layout.` });
      } else {
        setMessage({ type: 'error', text: res.message || 'Could not save the layout.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: messageOf(err, 'Could not save the layout.') });
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (code) => REPORT_FORMATS.find((f) => f.code === code)?.name || code;

  return (
    <div className="rfm-container">
      {message && <div className={`rfm-msg rfm-msg--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</div>}

      <div className="rfm-body">
        <aside className="rfm-centres" aria-label="Centres">
          <h2>Centres</h2>
          {loading && <p className="rfm-muted">Loading…</p>}
          {!loading && centres.length === 0 && <p className="rfm-muted">No centres found.</p>}
          <ul>
            {centres.map((c) => (
              <li key={c.centreId}>
                <button type="button" className={c.centreId === selectedId ? 'is-active' : ''} onClick={() => setSelectedId(c.centreId)}>
                  <strong>{c.centreName}</strong>
                  <span>{nameOf(c.formatCode)}{c.isDefault ? ' (default)' : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="rfm-main">
          {!selected ? <p className="rfm-muted">Select a centre.</p> : (
            <>
              <h2>{selected.centreName}</h2>
              <div className="rfm-options" role="radiogroup" aria-label="Report layout">
                {REPORT_FORMATS.map((f) => (
                  <label key={f.code} className={`rfm-option ${choice === f.code ? 'is-chosen' : ''}`}>
                    <input type="radio" name="report-layout" aria-label={f.name} checked={choice === f.code} onChange={() => setChoice(f.code)} />
                    <span className="rfm-option-name">{f.name}{selected.formatCode === f.code && !selected.isDefault ? <em> · current</em> : null}</span>
                    <span className="rfm-option-desc">{f.description}</span>
                  </label>
                ))}
              </div>

              <div className="rfm-actions">
                <button type="button" className="rfm-primary" onClick={save} disabled={!dirty || saving}>
                  {saving ? 'Saving…' : `Save layout for ${selected.centreName}`}
                </button>
                {!dirty && <span className="rfm-muted">No unsaved change.</span>}
              </div>

              <h3 className="rfm-preview-title">Preview with sample data</h3>
              <div className="rfm-preview">
                <div className="rfm-preview-scale">
                  <PathologyReportDocument
                    format={choice}
                    centre={selected}
                    patient={{ ...SAMPLE_PATIENT, centreName: selected.centreName }}
                    tests={SAMPLE_TESTS}
                    testObservationsMap={SAMPLE_OBS}
                    signature={SAMPLE_SIGNATURE}
                  />
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ReportFormatMasterPage;
